import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../../hooks/useLanguage';

interface SiteFooterProps {
  // Variant scopes the closing gradient block (MissionOS red, VMO blue,
  // FDA dark-green — the FDA variant clothes the whole site-end block so the
  // early-access CTA flows straight into partners + columns).
  variant?: 'default' | 'missionos' | 'vmo' | 'fda';
  // Hide the CTA block (Contact page hides it to avoid recursion).
  hideCta?: boolean;
  // Hide the partners block (variant pages render their own custom partners).
  hidePartners?: boolean;
  // Custom CTA / Partners content rendered inside the site-end wrapper.
  customCta?: React.ReactNode;
  customPartners?: React.ReactNode;
}

export const SiteFooterCols: React.FC = () => {
  const [language] = useLanguage();
  const en = language === 'en';
  return (
    <footer className="site-end__footer">
      <h2 className="visually-hidden">Site links</h2>
      <div className="site-end__cols">
        <div className="site-end__brand">
          <p className="site-end__brand-name">
            <Link to="/">Thoughtful Oasis</Link>
          </p>
          <p className="site-end__brand-tag">
            {en
              ? 'An AI research lab building infrastructure for trustworthy applications in mission-critical industries. Enschede, Netherlands.'
              : 'Een AI-onderzoekslab dat infrastructuur bouwt voor betrouwbare toepassingen in missiekritieke sectoren. Enschede, Nederland.'}
          </p>
          <span
            className="site-end__cert"
            role="img"
            aria-label="ISO 27001:2022 — gecertificeerd door Brand Compliance"
          />
        </div>
        <div className="site-end__col">
          <h3 className="site-end__col-head">{en ? 'Products' : 'Producten'}</h3>
          <ul>
            <li>
              <Link to="/platform">Platform</Link>
            </li>
            <li>
              <Link to="/applications">{en ? 'Applications' : 'Toepassingen'}</Link>
            </li>
            <li>
              <Link to="/mission-os">MissionOS</Link>
            </li>
            <li>
              <Link to="/vmo">VraagMijnOverheid</Link>
            </li>
          </ul>
        </div>
        <div className="site-end__col">
          <h3 className="site-end__col-head">{en ? 'Company' : 'Bedrijf'}</h3>
          <ul>
            <li>
              <Link to="/about">{en ? 'About' : 'Over ons'}</Link>
            </li>
            <li>
              <Link to="/research">{en ? 'Research' : 'Onderzoek'}</Link>
            </li>
            <li>
              <Link to="/news">{en ? 'News' : 'Nieuws'}</Link>
            </li>
            <li>
              <Link to="/documents">{en ? 'Documents' : 'Documenten'}</Link>
            </li>
          </ul>
        </div>
        <div className="site-end__col">
          <h3 className="site-end__col-head">{en ? 'Security' : 'Beveiliging'}</h3>
          <ul>
            <li>
              <a
                href="https://trust.thoughtful-oasis.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                {en ? 'Trust' : 'Vertrouwen'}
              </a>
            </li>
            <li>
              <Link to="/privacy-policy">{en ? 'Privacy policy' : 'Privacybeleid'}</Link>
            </li>
            <li>
              <Link to="/tos">{en ? 'Terms of service' : 'Algemene voorwaarden'}</Link>
            </li>
          </ul>
        </div>
      </div>
      <div className="site-end__base">
        <p className="site-end__copy">
          {en
            ? '© 2026 Thoughtful Oasis BV. All rights reserved.'
            : '© 2026 Thoughtful Oasis BV. Alle rechten voorbehouden.'}
        </p>
        <div className="site-end__social">
          <a
            href="https://www.linkedin.com/company/oasis-search/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="LinkedIn"
          >
            <svg className="site-end__icon" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.34V9h3.42v1.56h.05c.48-.9 1.65-1.85 3.39-1.85 3.62 0 4.29 2.38 4.29 5.48v6.26ZM5.34 7.43a2.07 2.07 0 1 1 0-4.15 2.07 2.07 0 0 1 0 4.15ZM7.12 20.45H3.55V9h3.57v11.45Z" />
            </svg>
            <span>LinkedIn</span>
          </a>
        </div>
      </div>
    </footer>
  );
};

const DefaultPartners: React.FC = () => {
  const [language] = useLanguage();
  const en = language === 'en';
  return (
    <div className="site-end__partners reveal">
      <p className="site-end__partners-label">{en ? 'Partners' : 'Partners'}</p>
      <div className="site-end__partners-logos">
        <a href="https://www.vnog.nl" target="_blank" rel="noopener noreferrer">
          <img
            src="/assets/website/logos/vnog.png"
            alt="Veiligheidsregio Noord- en Oost-Gelderland (VNOG) — logo"
          />
        </a>
        <a
          href="https://www.rijksoverheid.nl/ministeries/ministerie-van-justitie-en-veiligheid"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img
            src="/assets/website/logos/jenv-rijkslint.svg"
            alt="Ministerie van Justitie en Veiligheid"
          />
        </a>
        <a href="https://www.utwente.nl" target="_blank" rel="noopener noreferrer">
          <img src="/assets/website/logos/ut.png" alt="Universiteit Twente" />
        </a>
        <a href="https://www.eitdigital.eu" target="_blank" rel="noopener noreferrer">
          <img src="/assets/website/logos/eit-digital.png" alt="EIT Digital" />
        </a>
        <a href="https://aic4nl.org" target="_blank" rel="noopener noreferrer">
          <img src="/assets/website/logos/aic4nl.png" alt="AIC4NL" />
        </a>
        <a href="https://www.kickstartai.nl" target="_blank" rel="noopener noreferrer">
          <img src="/assets/website/logos/kickstart-ai.png" alt="Kickstart AI" />
        </a>
        <a href="https://novelt.com" target="_blank" rel="noopener noreferrer">
          <img src="/assets/website/logos/novelt.png" alt="NovelT" />
        </a>
      </div>
    </div>
  );
};

const DefaultCta: React.FC = () => {
  const [language] = useLanguage();
  const en = language === 'en';
  return (
    <div className="site-end__cta">
      <span className="site-end__label">{en ? 'Talk to us' : 'Praat met ons'}</span>
      <h2 className="site-end__title">
        {en
          ? "We work with a few partners at a time, on mission-critical problems. If you have one, let's talk."
          : 'We werken aan een paar partners tegelijk, aan missiekritieke problemen. Als jij er een hebt, laten we praten.'}
      </h2>
      <div className="site-end__action">
        <Link className="btn btn-primary" to="/contact">
          {en ? 'Contact us' : 'Neem contact op'}
        </Link>
      </div>
    </div>
  );
};

const SiteFooter: React.FC<SiteFooterProps> = ({
  variant = 'default',
  hideCta = false,
  hidePartners = false,
  customCta,
  customPartners,
}) => {
  const variantClass =
    variant === 'missionos'
      ? ' site-end--missionos'
      : variant === 'vmo'
      ? ' site-end--vmo'
      : variant === 'fda'
      ? ' site-end--fda'
      : '';

  const ctaNode = !hideCta ? customCta ?? <DefaultCta /> : null;
  const partnersNode = !hidePartners ? customPartners ?? <DefaultPartners /> : null;

  // Variant ordering: default + vmo render partners then cta; missionos + fda
  // render cta then partners (matches the standalone HTML markup — for FDA the
  // early-access CTA leads into partners + columns as one continuous block).
  const ctaFirst = variant === 'missionos' || variant === 'fda';

  return (
    <section className={`site-end${variantClass}`}>
      {ctaFirst ? ctaNode : partnersNode}
      {ctaFirst ? partnersNode : ctaNode}
      <SiteFooterCols />
    </section>
  );
};

export default SiteFooter;
